/**
 * Operations Dashboard API Functions
 */

import { api } from '@/lib/api';
import type {
  DashboardSummary,
  WorkloadItem,
  TimelineItem,
  ActivityItem,
  V4DashboardSummary,
  V4WorkloadItem,
  V4TimelineItem,
  V4ActivityItem,
  TaskStatus,
  TaskPriority,
} from './types';
import type { ProjectStatus, ProjectType } from '../operations-projects/types';

// =============================================================================
// Transform Functions
// =============================================================================

function transformV4Summary(v4: V4DashboardSummary): DashboardSummary {
  return {
    projects: {
      total: v4.projects.total,
      active: v4.projects.active,
      completed: v4.projects.completed,
      by_status: v4.projects.byStatus as Record<ProjectStatus, number>,
      by_type: v4.projects.byType as Record<ProjectType, number>,
      total_amount: v4.projects.totalAmount,
    },
    tasks: {
      total: v4.tasks.total,
      completed: v4.tasks.completed,
      overdue: v4.tasks.overdue,
      by_status: v4.tasks.byStatus as Record<TaskStatus, number>,
      by_priority: v4.tasks.byPriority as Record<TaskPriority, number>,
      unassigned: v4.tasks.unassigned,
    },
    completed_this_week: v4.completedThisWeek,
    completed_this_month: v4.completedThisMonth,
  };
}

function transformV4WorkloadItem(v4: V4WorkloadItem): WorkloadItem {
  return {
    assignee_id: v4.assigneeId,
    assignee_name: v4.assigneeName,
    task_count: v4.taskCount,
    overdue_count: v4.overdueCount,
    in_progress_count: v4.inProgressCount,
    todo_count: v4.todoCount,
    blocked_count: v4.blockedCount,
  };
}

function transformV4TimelineItem(v4: V4TimelineItem): TimelineItem {
  return {
    id: v4.id,
    type: v4.type,
    title: v4.title,
    due_date: v4.dueDate,
    status: v4.status,
    project_type: v4.projectType as ProjectType | undefined,
    priority: v4.priority as TaskPriority | undefined,
    assignee_name: v4.assigneeName,
  };
}

function transformV4ActivityItem(v4: V4ActivityItem): ActivityItem {
  return {
    id: v4.id,
    entity_type: v4.entityType,
    entity_id: v4.entityId,
    entity_title: v4.entityTitle,
    action: v4.action,
    details: v4.details,
    actor_name: v4.actorName,
    created_at: v4.createdAt,
  };
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Fetch dashboard summary statistics
 */
export async function fetchDashboardSummary(): Promise<DashboardSummary> {
  const data = await api.get<V4DashboardSummary>('/v4/operations-metrics/summary');
  return transformV4Summary(data);
}

/**
 * Fetch team workload distribution
 */
export async function fetchWorkload(): Promise<WorkloadItem[]> {
  const data = await api.get<V4WorkloadItem[]>('/v4/operations-metrics/workload');
  return data.map(transformV4WorkloadItem);
}

/**
 * Fetch upcoming deadlines
 * @param days - Number of days to look ahead (default 14)
 */
export async function fetchTimeline(days: number = 14): Promise<TimelineItem[]> {
  const data = await api.get<V4TimelineItem[]>('/v4/operations-metrics/timeline', { days });
  return data.map(transformV4TimelineItem);
}

/**
 * Fetch recent activity
 * @param limit - Number of items to return (default 20)
 */
export async function fetchActivity(limit: number = 20): Promise<ActivityItem[]> {
  const data = await api.get<V4ActivityItem[]>('/v4/operations-metrics/activity', { limit });
  return data.map(transformV4ActivityItem);
}

import type { ProjectsKanbanData, KanbanQueryParams, AdvanceStageInput, UpdateProjectPositionsInput } from './types';

export async function fetchProjectsKanban(params: KanbanQueryParams = {}): Promise<ProjectsKanbanData> {
  return api.get<ProjectsKanbanData>('/v4/quotes/kanban', params as Record<string, unknown>);
}

export async function advanceProjectStage(input: AdvanceStageInput): Promise<unknown> {
  return api.put('/v4/quotes/kanban/advance', input);
}

export async function updateProjectPositions(input: UpdateProjectPositionsInput): Promise<unknown> {
  return api.put('/v4/quotes/positions', input);
}

export async function updateQuotePriority(quoteId: string, priority: 'urgent' | 'normal' | 'low'): Promise<unknown> {
  return api.put(`/v4/quotes/${quoteId}`, { priority });
}

// Flashed transmitters (dev_id = MAC) for a quote — feeds the shipping-stage MAC validation window.
export interface FlashedTransmitter {
  dev_id: string;
  name?: string;
  product?: string;
  quote_number?: string;
  activated?: boolean;
  created_at?: string;
}

export interface TransmittersByQuoteResponse {
  transmitters: FlashedTransmitter[];
  total: number;
}

export async function fetchTransmittersByQuote(quoteNumber: string): Promise<TransmittersByQuoteResponse> {
  return api.get<TransmittersByQuoteResponse>(`/v3/transmitters/by-quote/${encodeURIComponent(quoteNumber)}`);
}

// Confirmed device set for a quote (`quote_device_links`) — the durable record behind the
// shipping-stage MAC validation window (C2a). Distinct from FlashedTransmitter above (C1):
// C1 reads `transmitter.quote_number`, which is stamped at flash time ONLY when a quote
// number was actually typed, and which nothing ever clears — a device coming online does not
// unlink it. So a device missing from C1 was never stamped, not "unlinked". The confirmed set
// here persists independently (soft-delete + re-insert on re-confirm).
export interface QuoteDeviceLink {
  id: string;
  dev_id: string;
  sensor_tech?: string | null;
  device_type?: string | null;
  confirmed_by_name?: string | null;
  confirmed_at?: string | null;
}

export interface QuoteDevicesResponse {
  devices: QuoteDeviceLink[];
}

export interface DeviceConfirmInput {
  devId: string;
  deviceType?: string;
  sensorTech?: string;
}

export async function fetchQuoteDevices(quoteId: string): Promise<QuoteDevicesResponse> {
  return api.get<QuoteDevicesResponse>(`/v4/quotes/${encodeURIComponent(quoteId)}/devices`);
}

export async function confirmQuoteDevices(
  quoteId: string,
  devices: DeviceConfirmInput[],
  confirmedByName?: string,
): Promise<QuoteDevicesResponse> {
  return api.post<QuoteDevicesResponse>(`/v4/quotes/${encodeURIComponent(quoteId)}/devices`, {
    devices,
    ...(confirmedByName ? { confirmedByName } : {}),
  });
}
